import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { TbDots, TbUserPlus, TbTrashFilled } from "react-icons/tb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// @ts-ignore - Prisma client types
import type {  User  } from '@prisma/client';
import { BasicUserType } from "@/types/user";
import moment from "moment";

interface PostHeaderProps {
  name: string;
  username: string;
  image: string | null;
  createdAt: Date;
  currentUserId?: string;
  userId: string;
  isFollowing: boolean;
  targetUser: User | null;
  onFollowToggle: () => void;
  onDelete: () => void;
  onProfileClick?: (username: string, profile?: BasicUserType) => void;
}

export function PostHeader({
  name,
  username,
  image,
  createdAt,
  currentUserId,
  userId,
  isFollowing,
  targetUser,
  onFollowToggle,
  onDelete,
  onProfileClick,
}: PostHeaderProps) {

  return (
    <div className="flex gap-2 font-semibold w-full">
      <div className="flex gap-2">
        <HoverCard>
          <HoverCardTrigger asChild>
            <div
              data-interactive="true"
              className="flex flex-col cursor-pointer"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                if (onProfileClick) {
                  // Convert targetUser to BasicUserType if available
                  const profile: BasicUserType | undefined = targetUser ? {
                    id: targetUser.id?.toString() || '',
                    name: targetUser.name || '',
                    username: targetUser.username || '',
                    email: targetUser.email || '',
                    emailVerified: targetUser.emailVerified || null,
                    image: targetUser.image || null,
                    hashedPassword: targetUser.hashedPassword || '',
                    bio: targetUser.bio || '',
                    bioLink: targetUser.bioLink || null,
                    profileURL: targetUser.profileURL || null,
                    followers: Array.isArray(targetUser.followers) ? targetUser?.followers?.map(id => id?.toString() || '') : [],
                    following: Array.isArray(targetUser.following) ? targetUser?.following?.map(id => id?.toString() || '') : [],
                    createdAt: targetUser.createdAt || new Date(),
                    updatedAt: targetUser.updatedAt || new Date(),
                    emailVerificationToken: targetUser.emailVerificationToken || null,
                    emailVerificationTokenExpiry: targetUser.emailVerificationTokenExpiry || null,
                    passwordResetToken: targetUser.passwordResetToken || null,
                    passwordResetTokenExpiry: targetUser.passwordResetTokenExpiry || null,
                    suspended: targetUser.suspended || false,
                  } : undefined;
                  onProfileClick(username, profile);
                }
              }}
            >
              <span className="hover:underline">
                {name}
              </span>
              <span className="text-[#71767B] font-normal text-sm">@{username}</span>
            </div>
          </HoverCardTrigger>
          <HoverCardContent 
            className="w-80" 
            align="center" 
            sideOffset={4}
            props={[]}
          >
            <div className="flex justify-between space-x-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={image || ""} />
                <AvatarFallback>{name[0]}</AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">{name}</h4>
                <p className="text-sm text-gray-500">@{username}</p>
                {targetUser && (
                  <div className="text-sm text-gray-500">
                    <p>{targetUser.followers?.length || 0} followers</p>
                  </div>
                )}
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
        <span className="text-[#71767B]">·</span>
        <span className="text-[#71767B]">
          {moment(new Date(createdAt)).fromNow(true)}
        </span>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto relative z-20"
            data-interactive="true"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation(); // Prevent post navigation
            }}
          >
            <TbDots className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuGroup>
            {currentUserId !== userId && (
              <DropdownMenuItem
                onClick={onFollowToggle}
                className={isFollowing ? "text-blue-500" : ""}
              >
                <TbUserPlus className="mr-2 h-4 w-4" />
                <span>{isFollowing ? "Unfollow" : "Follow"}</span>
              </DropdownMenuItem>
            )}
            {currentUserId === userId && (
              <DropdownMenuItem
                className="text-red-500 focus:text-red-500"
                onClick={onDelete}
              >
                <TbTrashFilled className="mr-2 h-4 w-4" />
                <span>Delete</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}